import type React from 'react';
import { ESensorModel } from '@/types';

/**
 * Normalize model strings to handle backend variations:
 * "Heatic 2+", "Heatic2+", "Heatic 2 Plus", "heatic 2+" all map to "heatic2plus"
 */
export function normalizeModel(model: string | null | undefined): string {
  return (model ?? '').toLowerCase().replace(/\s+/g, '').replace(/\+/g, 'plus');
}

export const HEATIC2_PLUS_NORMALIZED = normalizeModel(ESensorModel.HEATIC2_PLUS);

/** Standard icon size (px) used inside sensor Tag components */
export const TAG_ICON_SIZE = 14;

/** Standard width (px) for tooltip/popover content panels in studio views */
export const TOOLTIP_CONTENT_WIDTH = 220;

/** Reusable style for vertical Ant Design Dividers used as inline separators */
export const VERTICAL_DIVIDER_STYLE: React.CSSProperties = { margin: 0, top: 0, height: '1em' };

/** Reusable inline-flex row style for sensor Tag content (icon + label).
 * Background uses antd's `colorFillContent` token so the tag stays visible
 * on the `colorBgLayout` grey banner — antd's default Tag bg
 * (`colorFillQuaternary`) fades into the surrounding surface. */
export const SENSOR_TAG_STYLE: React.CSSProperties = {
  margin: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  backgroundColor: 'var(--ant-color-fill-content)',
};

export interface SensorFlags {
  hasPir: boolean;
  hasTraffic: boolean;
  hasPresence: boolean;
  hasMotion?: boolean;
  hasDesk?: boolean;
}

/**
 * Per-room aggregation of sensor flags plus optional counts and an `hasEntrance`
 * flag for the historical dashboard's room/zone table. Extending `SensorFlags`
 * (rather than redeclaring the boolean fields) keeps the two surfaces in sync
 * if anyone adds a new data type later.
 */
export interface RoomSensorInfo extends SensorFlags {
  hasEntrance?: boolean;
  presenceCount?: number;
  trafficCount?: number;
  motionCount?: number;
  deskCount?: number;
}

export type DataTypeKey = 'Presence' | 'Motion' | 'PIR Zeroing' | 'Traffic' | 'Desk';

export interface DataTypeInfo {
  label: DataTypeKey;
  color: string;
}

const DATA_TYPE_PRIORITY: { flag: keyof SensorFlags; label: DataTypeKey; color: string }[] = [
  { flag: 'hasPir', label: 'PIR Zeroing', color: 'cyan' },
  { flag: 'hasTraffic', label: 'Traffic', color: 'blue' },
  { flag: 'hasPresence', label: 'Presence', color: 'purple' },
  { flag: 'hasMotion', label: 'Motion', color: 'orange' },
  { flag: 'hasDesk', label: 'Desk', color: 'lime' },
];

/** Color lookup by data type label (includes 'No Data' fallback). Derived from DATA_TYPE_PRIORITY. */
export const DATA_TYPE_TAG_COLOR: Record<DataTypeKey | 'No Data', string> = {
  ...Object.fromEntries(DATA_TYPE_PRIORITY.map(({ label, color }) => [label, color])),
  'No Data': 'default',
} as Record<DataTypeKey | 'No Data', string>;

/** Derive the primary data type from sensor flags. Single source of truth for priority order. */
export function getDataTypeFromFlags(flags: SensorFlags): DataTypeInfo | undefined {
  for (const { flag, label, color } of DATA_TYPE_PRIORITY) {
    if (flags[flag]) return { label, color };
  }
  return undefined;
}

/**
 * Derive all active data types from sensor flags.
 * PIR Zeroing is treated as a single override (it subsumes Traffic + Presence).
 * Otherwise all matching types are returned.
 */
export function getDataTypesFromFlags(flags: SensorFlags): DataTypeInfo[] {
  if (flags.hasPir) return [{ label: 'PIR Zeroing', color: DATA_TYPE_TAG_COLOR['PIR Zeroing'] }];
  return DATA_TYPE_PRIORITY.filter(({ flag, label }) => label !== 'PIR Zeroing' && flags[flag]).map(
    ({ label, color }) => ({ label, color }),
  );
}

/** Pluralize a word: `pluralize(3, 'sensor')` → `'3 sensors'` */
export function pluralize(count: number, singular: string): string {
  return `${count} ${singular}${count !== 1 ? 's' : ''}`;
}

/** Sensor with health status fields needed by computeHealthCounts */
export interface SensorHealthRecord {
  isSleepTime: boolean;
  connectionHealth?: { status: string } | null;
  deviceStatus?: 'ACTIVE' | 'ARCHIVED' | null;
}

/** Count offline and warning sensors from a list of sensor health records */
export function computeHealthCounts(sensors: SensorHealthRecord[]): {
  offlineCount: number;
  warningCount: number;
} {
  let offlineCount = 0;
  let warningCount = 0;
  for (const s of sensors) {
    if (s.deviceStatus === 'ARCHIVED') continue;
    if (s.isSleepTime || s.connectionHealth?.status === 'CONNECTED') continue;
    else if (s.connectionHealth?.status === 'NOT_CONNECTED') offlineCount++;
    else if (s.connectionHealth?.status === 'PARTIALLY_CONNECTED') warningCount++;
  }
  return { offlineCount, warningCount };
}

/** Sensor type badge config (without icon — consumers attach their own React icon nodes) */
export interface SensorBadgeConfig {
  label: string;
  count: number;
  color: string;
}

/** Build sensor type badge configs from counts. Returns only types with count > 0. */
export function buildSensorBadgeConfigs(counts: {
  traffic: number;
  presence: number;
  motion: number;
  desk: number;
}): SensorBadgeConfig[] {
  const badges: SensorBadgeConfig[] = [];
  if (counts.traffic > 0)
    badges.push({ label: 'Traffic', count: counts.traffic, color: 'default' });
  if (counts.presence > 0)
    badges.push({ label: 'Presence', count: counts.presence, color: 'default' });
  if (counts.motion > 0) badges.push({ label: 'Motion', count: counts.motion, color: 'default' });
  if (counts.desk > 0) badges.push({ label: 'Desk', count: counts.desk, color: 'default' });
  return badges;
}
